// src/oura/newscan/fixtures/render.ts
//
// Plans the 50 for a business (which template goes in which slot, for which
// outlet or town) and renders a template with the words. Pure: the same inputs
// always give the same rows, in the same order.
//
// Row keys are stable across redrafts and outlet reorders, so a staff edit is
// stored against the key and survives renumbering:
//   L<angle>@<area>   a location angle at one outlet ("L2@Bangsar")
//   C<slot> / X<i>    a city-level slot / a single-outlet extra
//   B:<id>            a brand row (v4.5, Online): a sample's pre-authored row (brand.ts)

import type { ScanDraftBusiness, ScanQuestionCheck } from "@service/aeo/scanDraftTypes";
import { brandPlan } from "./brand";
import type { BrandRowDef, BrandSet } from "./brandSamples";
import {
    CITY_SLOTS,
    HOME_CITY_SWAPS,
    HOME_SWAPS,
    LOCAL_ANGLES,
    LOCATION_TOTAL,
    MS_SWAPS,
    OUTLETS_TRANSLATED,
    PLACE_VARS,
    SINGLE_EXTRAS,
    SINGLE_LOCATION_TOTAL,
    ZH_SWAPS,
    outletShares,
    templateVars,
    type TemplateDef,
} from "./recipe";
import type { PlaceFacts } from "./samples";

export type RowLanguage = "en" | "ms" | "zh";

export interface PlannedRow {
    key: string;
    check: ScanQuestionCheck;
    group: string;
    /** The template the recipe puts in this slot. */
    template: TemplateDef;
    /** Place bindings: area and what is near it, city, or market. */
    ctx: Record<string, string>;
    town: string | null;
    /** Location rows: the outlet area or the market the row asks about. */
    place: string | null;
    language: RowLanguage;
    /** Brand rows: the market code the row asks for (us, my, sg, au, uk, global). */
    market?: string;
    /** Brand rows: the Google suggestion behind it, null when none supports it. */
    trace?: { suggestion: string; market: string } | null;
    /** Brand rows: the authored row it comes from. */
    brand?: BrandRowDef;
}

export interface Place {
    name: string;
    town: string;
}

export interface PlanInput {
    business: ScanDraftBusiness;
    places: Record<string, PlaceFacts>;
    languagesOn: { ms: boolean; zh: boolean };
    /** Whether a word has a value: a language row is only used when every word it needs exists. */
    hasWord: (variable: string) => boolean;
    /** Online (brand mode): the sample's pre-authored set. None: no rows (the draft fails with a plain sentence). */
    brand?: BrandSet | null;
    /** Brand: how many times the searches were pulled again. */
    demandRound?: number;
    /** Brand: the share of buyers who search in Malay and Chinese. */
    languageShare?: { ms: number; zh: number };
}

const CHECK_ORDER: Record<ScanQuestionCheck, number> = { location: 0, specialty: 1, intent: 2, pick: 0, source: 1, supplier: 2 };

/** The outlets that get questions: the ones the site names, and staff ones staff confirmed. */
function activePlaces(business: ScanDraftBusiness): Place[] {
    return business.outlets
        .filter((o) => o.name.trim() && (o.source === "site" || o.confirmed === true))
        .map((o) => ({ name: o.name.trim(), town: (o.town || business.city || o.name).trim() }));
}

/** The places a location question may name: the outlets (showrooms for Both), or the markets for Online. */
export function placeNames(business: ScanDraftBusiness): string[] {
    const market = business.market || "Malaysia";
    if (business.buy === "online") return (business.markets ?? []).map((m) => m.label).concat(business.markets?.length ? [] : [market]);
    const places = activePlaces(business);
    return places.length ? places.map((p) => p.name) : [business.city].filter(Boolean);
}

/**
 * An outlet's place bindings. With no known building, station or landmark the
 * area itself stands in ("near Cheras"), as the backend's fallbacks do, rather
 * than borrowing another outlet's mall.
 */
function placeCtx(place: Place, facts: Record<string, PlaceFacts>): Record<string, string> {
    const near = facts[place.name.toLowerCase()] ?? {};
    return {
        area: place.name,
        area_building: near.building || place.name,
        area_transit: near.transit || place.name,
        area_nearby_landmark: near.landmark || place.name,
    };
}

/**
 * Spread the city-level slots over the outlets' towns in proportion to their
 * location share, never the same town twice in a row when there is a choice.
 */
function townRotation(places: Place[], shares: number[], count: number): string[] {
    const weight: Record<string, number> = {};
    places.forEach((p, i) => {
        weight[p.town] = (weight[p.town] ?? 0) + (shares[i] ?? 0);
    });
    const towns = Object.keys(weight);
    if (!towns.length) return Array.from({ length: count }, () => "");
    const total = shares.reduce((a, b) => a + b, 0) || 1;
    const got: Record<string, number> = Object.fromEntries(towns.map((t) => [t, 0]));
    const out: string[] = [];
    for (let k = 0; k < count; k++) {
        let best = towns[0];
        let bestScore = -1e9;
        for (const t of towns) {
            const repeat = towns.length > 1 && out[k - 1] === t ? 0.5 : 0;
            const score = (weight[t] / total) * (k + 1) - got[t] - repeat;
            if (score > bestScore) {
                bestScore = score;
                best = t;
            }
        }
        out.push(best);
        got[best] += 1;
    }
    return out;
}

function localPlan(input: PlanInput): PlannedRow[] {
    const { business } = input;
    const home = business.buy === "we_go_to_them";
    let places = activePlaces(business);
    if (!places.length) places = [{ name: business.city.trim(), town: business.city.trim() }];
    const single = places.length === 1;
    const shares = outletShares(places.length, single ? SINGLE_LOCATION_TOTAL : LOCATION_TOTAL);
    const rows: PlannedRow[] = [];

    places.forEach((place, index) => {
        const ctx = placeCtx(place, input.places);
        LOCAL_ANGLES.slice(0, shares[index]).forEach((angle) => {
            rows.push({
                key: `${angle.id}@${place.name}`,
                check: "location",
                group: index === 0 ? "anchor" : "outlets",
                template: home && HOME_SWAPS[angle.id] ? HOME_SWAPS[angle.id] : angle,
                ctx,
                town: place.town,
                place: place.name,
                language: "en",
            });
        });
    });

    const entries = single ? [...CITY_SLOTS, ...SINGLE_EXTRAS] : CITY_SLOTS;
    const towns = townRotation(places, shares, entries.length);
    entries.forEach((template, i) => {
        const used = home && HOME_CITY_SWAPS[template.id] ? HOME_CITY_SWAPS[template.id] : template;
        rows.push({
            key: template.id,
            check: used.check,
            group: used.group,
            template: used,
            ctx: { city: towns[i] },
            town: towns[i],
            place: null,
            language: "en",
        });
    });

    applyLanguages(rows, input, places);
    return rows;
}

/** Tier-1 Malay and Chinese rows: only on the first two outlets, only when every word exists. */
function applyLanguages(rows: PlannedRow[], input: PlanInput, places: Place[]): void {
    const translated = new Set(places.slice(0, OUTLETS_TRANSLATED).map((p) => p.name));
    const swap = (map: Record<string, TemplateDef>, language: RowLanguage) => {
        rows.forEach((row) => {
            if (row.language !== "en") return;
            const base = row.key.split("@")[0];
            const target = map[base];
            if (!target) return;
            if (row.check === "location" && !(row.place && translated.has(row.place))) return;
            const words = templateVars(target.text).filter((v) => !PLACE_VARS.has(v));
            if (!words.every((v) => input.hasWord(v))) return;
            row.template = { ...target, check: row.check, group: row.group };
            row.language = language;
        });
    };
    if (input.languagesOn.ms) swap(MS_SWAPS, "ms");
    if (input.languagesOn.zh) swap(ZH_SWAPS, "zh");
}

/** The 50 slots for a business, location first, then specialty, then intent. */
export function planRows(input: PlanInput): PlannedRow[] {
    const buy = input.business.buy;
    // Brand rows come in their own order (group, then market priority): never re-sorted here.
    if (buy === "online") {
        if (!input.brand) return [];
        return brandPlan({
            business: input.business,
            set: input.brand,
            languagesOn: input.languagesOn,
            languageShare: input.languageShare ?? { ms: 0, zh: 0 },
            demandRound: input.demandRound ?? 0,
        });
    }
    // Visit us, Both (the showrooms are its outlets) and We go to them: the local recipe.
    return localPlan(input)
        .map((row, i) => ({ row, i }))
        .sort((a, b) => CHECK_ORDER[a.row.check] - CHECK_ORDER[b.row.check] || a.i - b.i)
        .map(({ row }) => row);
}

export interface RenderedText {
    text: string;
    fill_vars: string[];
    fill_values: Record<string, string>;
    /** Words the template needs that have no value: the text keeps their {placeholder}. */
    missing: string[];
}

/** Fill a template: place bindings first, then the words. A blank word keeps its {placeholder}. */
export function renderTemplate(text: string, ctx: Record<string, string>, word: (variable: string) => string): RenderedText {
    const parts = text.split(/(\{[a-z_0-9]+\})/);
    const fillVars: string[] = [];
    const fillValues: Record<string, string> = {};
    const missing: string[] = [];
    let out = "";
    parts.forEach((part, i) => {
        const m = /^\{([a-z_0-9]+)\}$/.exec(part);
        if (!m) {
            // "{place_noun}s" after a word that already ends in s: "mattresses", not "mattresss".
            out += /^s\b/.test(part) && /(s|x|ch|sh)$/i.test(out) && i > 0 ? `e${part}` : part;
            return;
        }
        const variable = m[1];
        const value = ctx[variable] !== undefined ? ctx[variable] : word(variable);
        if (!fillVars.includes(variable)) fillVars.push(variable);
        fillValues[variable] = value;
        if (!value) {
            if (!missing.includes(variable)) missing.push(variable);
            out += part;
            return;
        }
        out += out === "" && /^[a-z]/.test(value) ? value[0].toUpperCase() + value.slice(1) : value;
    });
    return { text: out, fill_vars: fillVars, fill_values: fillValues, missing };
}

/** What a row is "about": the outlet for location rows, else the first word it is filled with. */
export function fillOf(row: PlannedRow, rendered: RenderedText): string {
    if (row.place) return row.place;
    const first = rendered.fill_vars.find((v) => !PLACE_VARS.has(v) && rendered.fill_values[v]);
    return first ? rendered.fill_values[first] : row.group;
}
